/**
 * Client-side replication of the Groovy DocTypeExtractor pattern → bounding box pipeline.
 *
 * Text lines  →  pattern match  →  calcRectangleFromPositions  →  normalised PatternBox
 *
 * Legacy rules (DocTypeExtractor.groovy):
 * - Boundary patterns (lineItemsHeader/FooterPattern): lineStr.matches(pattern)
 *   — Java String.matches() = full-string anchor (implicit ^...$).
 * - Field patterns (orderNumberPattern etc.): lineStr =~ pattern
 *   — Groovy find operator = partial match anywhere in the string.
 * - calcRectangleFromPositions(first, last, adj=2):
 *     x = trunc(first.x - 2)
 *     y = trunc((pageHeight - first.ty) - first.height - 2)
 *     w = trunc(last.x - first.x + last.width + 4)
 *     h = trunc(first.height + 4)
 * - calculateRectangleOfMatchedText: strips spaces from captured group,
 *   walks chars to find the contiguous run, boxes just that span.
 */

export interface TextItem {
  str: string;
  transform: number[]; // [scaleX, skewY, skewX, scaleY, tx, ty]
  width: number;
  height: number;
}

export interface TextLine {
  str: string;
  items: TextItem[];
}

export interface PatternBox {
  x: number;
  y: number;
  w: number;
  h: number;
  page: number;
}

export const LINE_Y_TOLERANCE = 2; // px — items within this y-distance are the same line

export function groupItemsIntoLines(items: TextItem[]): TextLine[] {
  const lines: { y: number; items: TextItem[] }[] = [];
  for (const item of items) {
    if (!item.str.trim()) continue;
    const y = item.transform[5];
    const existing = lines.find((l) => Math.abs(l.y - y) <= LINE_Y_TOLERANCE);
    if (existing) {
      existing.items.push(item);
    } else {
      lines.push({ y, items: [item] });
    }
  }
  lines.sort((a, b) => b.y - a.y);
  for (const line of lines) {
    line.items.sort((a, b) => a.transform[4] - b.transform[4]);
  }
  // Join without separator — item.str already carries any trailing space from the PDF.
  return lines.map((l) => ({ str: l.items.map((i) => i.str).join(''), items: l.items }));
}

// fullString=true → Java String.matches (implicit ^...$)
// fullString=false → Groovy =~ find (partial)
export function buildRegex(pattern: string, fullString: boolean): RegExp {
  const flagMatch = pattern.match(/^\(\?([a-z]+)\)/);
  const flags = flagMatch ? flagMatch[1] : '';
  const core = flagMatch ? pattern.slice(flagMatch[0].length) : pattern;
  return fullString ? new RegExp(`^(?:${core})$`, flags) : new RegExp(core, flags);
}

// Groovy calcRectangleFromPositions(first, last, adj=2) + normalise.
export function calcBoxFromItems(
  first: TextItem,
  last: TextItem,
  pageHeight: number,
  pdfWidth: number,
  pdfHeight: number
): PatternBox {
  const fx = first.transform[4];
  const fy = pageHeight - first.transform[5];
  const fh = first.height || last.height || 10;
  const lx = last.transform[4];
  const lw = last.width;

  const px = Math.trunc(fx - 2);
  const py = Math.trunc(fy - fh - 2);
  const pw = Math.trunc(lx - fx + lw + 4);
  const ph = Math.trunc(fh + 4);

  return {
    x: Math.max(0, px / pdfWidth),
    y: Math.max(0, py / pdfHeight),
    w: Math.min(1, pw / pdfWidth),
    h: Math.min(1, ph / pdfHeight),
    page: 1,
  };
}

// Full-line box — boundary patterns (header/footer).
export function calcPatternBox(
  line: TextLine,
  pageHeight: number,
  pdfWidth: number,
  pdfHeight: number
): PatternBox | null {
  if (!line.items.length || !pdfWidth || !pdfHeight) return null;
  return calcBoxFromItems(line.items[0], line.items[line.items.length - 1], pageHeight, pdfWidth, pdfHeight);
}

// Group-1 box — replicates calculateRectangleOfMatchedText.
// Strips spaces from the captured value, walks items to find the contiguous run.
export function calcCaptureGroupBox(
  line: TextLine,
  capturedValue: string,
  pageHeight: number,
  pdfWidth: number,
  pdfHeight: number
): PatternBox | null {
  if (!capturedValue || !line.items.length || !pdfWidth || !pdfHeight) return null;
  const target = capturedValue.replace(/\s+/g, '');
  if (!target) return null;

  let accumulated = '';
  let startItem: TextItem | null = null;
  let lastItem: TextItem | null = null;

  for (const item of line.items) {
    const noSpace = item.str.replace(/\s+/g, '');
    if (!noSpace) continue;

    const combined = accumulated + noSpace;
    if (target.startsWith(combined)) {
      if (!startItem) startItem = item;
      accumulated = combined;
      lastItem = item;
    } else {
      // Lost the run — restart from this item
      startItem = null;
      accumulated = '';
      lastItem = null;
      if (target.startsWith(noSpace)) {
        startItem = item;
        accumulated = noSpace;
        lastItem = item;
      }
    }

    if (accumulated === target && startItem && lastItem) {
      return calcBoxFromItems(startItem, lastItem, pageHeight, pdfWidth, pdfHeight);
    }
  }
  return null;
}
